import {
  InputPercentage,
  InputNote,
  InputNumber,
  InputCurrency,
} from "@sellar-io/components";
import { useEffect } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { SELLAR_DISCOUNT_TYPES } from "../../../../../constants/sellarConstants.shared";
import { formatPriceInputToApi } from "../../../../../hooks/usePriceFormat";

type PriceTierDiscountItemType =
  | "can"
  | "bottle"
  | "keg"
  | "cask"
  | "draught"
  | "bagInBox";

export type PriceTierDiscountFormValues = {
  discountByType: "minimumOrderQuantity" | "minimumBasketValue";
  type: (typeof SELLAR_DISCOUNT_TYPES)[keyof typeof SELLAR_DISCOUNT_TYPES]["value"];
  minimumBasketValue: number;
  itemType: PriceTierDiscountItemType | "any";
  quantity: number;
  amount: string;
};

export const PriceTierDiscountForm = ({
  form,
  formId,
  onSave,
}: {
  form: UseFormReturn<PriceTierDiscountFormValues>;
  formId: string;
  onSave: (
    data: Omit<
      PriceTierDiscountFormValues,
      "minimumBasketValue" | "amount" | "itemType" | "discountByType"
    > & {
      minimumBasketValue: number;
      amount: number;
      itemType: PriceTierDiscountItemType | null;
    },
  ) => void;
}) => {
  const discountByType = useWatch({
    control: form.control,
    name: "discountByType",
  });
  const type = useWatch({ control: form.control, name: "type" });

  useEffect(() => {
    if (discountByType === "minimumOrderQuantity") {
      form.setValue("minimumBasketValue", 0);
    } else {
      form.setValue("quantity", 0);
    }
  }, [form, discountByType, type]);

  return (
    <form
      className={"flex flex-col gap-2"}
      id={formId}
      onSubmit={form.handleSubmit((data) => {
        const { type } = data;

        const amount =
          data.type === "percentage"
            ? parseFloat(data.amount)
            : (formatPriceInputToApi(data.amount) ?? 0);

        const itemType = data.itemType === "any" ? null : data.itemType;

        onSave({
          type,
          itemType,
          amount,
          ...(data.quantity
            ? {
                quantity: data.quantity,
                minimumBasketValue: 0,
              }
            : {
                minimumBasketValue:
                  formatPriceInputToApi(data.minimumBasketValue) ?? 0,
                quantity: 0,
              }),
        });
      })}
    >
      <InputLabel>Discount rule</InputLabel>
      <InputContainer>
        <InputSelect
          {...form.register("discountByType", {
            required: true,
          })}
        >
          <InputOption value="minimumBasketValue">
            By minimum basket value
          </InputOption>
          <option value="minimumOrderQuantity">
            By minimum order quantity
          </option>
        </InputSelect>
      </InputContainer>

      <InputLabel>Container type</InputLabel>
      <InputContainer>
        <InputSelect
          {...form.register("itemType", {
            required: true,
          })}
        >
          <InputOption key={"any"} value={"any"}>
            Any
          </InputOption>
          <InputOption key={"can"} value={"can"}>
            Cases of cans
          </InputOption>
          <InputOption key={"bottle"} value={"bottle"}>
            Cases of bottles
          </InputOption>
          <InputOption key={"keg"} value={"keg"}>
            Kegs
          </InputOption>
          <InputOption key={"cask"} value={"cask"}>
            Casks
          </InputOption>
          <InputOption key={"draught"} value={"draught"}>
            Draught items (keg or cask)
          </InputOption>
          <InputOption key={"bagInBox"} value={"bagInBox"}>
            Bag In Box
          </InputOption>
        </InputSelect>
      </InputContainer>

      {discountByType === "minimumOrderQuantity" && (
        <>
          <InputLabel>Container quantity</InputLabel>
          <InputContainer>
            <InputNumber
              {...form.register("quantity", {
                required: true,
                min: 1,
                valueAsNumber: true,
              })}
              placeholder={"5"}
            />
          </InputContainer>
        </>
      )}

      {discountByType === "minimumBasketValue" && (
        <>
          <InputLabel>Minimum basket value</InputLabel>
          <InputContainer>
            <InputCurrency
              showPrefix
              allowNegative
              {...form.register("minimumBasketValue", {
                required: true,
                min: 0.01,
              })}
              placeholder="250"
            />
          </InputContainer>
          <InputNote>Minimum £0.01</InputNote>
        </>
      )}

      <>
        <InputLabel>Discount type</InputLabel>
        <InputContainer>
          <InputSelect {...form.register("type", { required: true })}>
            <InputOption value={"percentage"}>Percentage</InputOption>
            <InputOption value={"perpetual-flat"}>Flat (per item)</InputOption>
          </InputSelect>
        </InputContainer>
      </>

      {type === SELLAR_DISCOUNT_TYPES.PERCENTAGE.value || !type ? (
        <>
          <InputLabel>Discount percentage</InputLabel>
          <InputContainer>
            <InputPercentage
              placeholder={"15%"}
              step={"0.1"}
              {...form.register("amount", {
                min: 1,
                max: 100,
                required: true,
                valueAsNumber: true,
              })}
            />
            <span>%</span>
          </InputContainer>
        </>
      ) : (
        <>
          <InputLabel>Discount value</InputLabel>
          <InputContainer>
            <InputCurrency
              showPrefix
              allowNegative
              placeholder={"10"}
              {...form.register("amount", {
                required: true,
                valueAsNumber: true,
              })}
            />
          </InputContainer>
        </>
      )}
    </form>
  );
};
